<template>
  <div>
    <div class="row">
      <div class="col-12">
        <Card body-classes="p-0" class="mb-5">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between">
              <span>
                {{ category.name }} Category Products ({{dataTable.length}})
                <span class="pb-5">
                  <button
                      v-if="profile.role !== 'user'"
                      class="btn btn-success btn-sm"
                      @click="addProduct"><i class="fa fa-plus"></i> Add Product
                  </button>
                </span>
              </span>
              <UInput v-model="dataTableSearch" size="md" placeholder="Search products" class="mb-0 w-25"/>
            </div>
          </template>
          <div v-if="loading" class="text-center py-5">
            <Spinner spinner-class="mb-1" />
            <p class="text-muted mt-2">Loading products...</p>
          </div>
          <div v-if="!loading" class="card border-0 shadow-sm mb-4">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>CODE</th>
                    <th>NAME</th>
                    <th>BUYING PRICE</th>
                    <th>SELLING PRICE</th>
                    <th>EXPECTED MARGIN</th>
                    <th>ACTUAL MARGIN</th>
                    <th class="text-end">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(product, index) in dataTable" :key="product.id" class="product-row" :class="{'bg-light': index % 2 === 0}">
                    <td>{{ product.sku }}</td>
                    <td>{{ product.code }}</td>
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="product-avatar me-2" :class="`bg-${['primary','success','info','warning'][index % 4]}-light`">
                          <span class="avatar-text">{{ product.name.charAt(0).toUpperCase() }}</span>
                        </div>
                        <span class="fw-medium">{{ product.name }}</span>
                      </div>
                    </td>
                    <td>{{ formatNumber(product.buy) }}</td>
                    <td>{{ formatNumber(product.sell) }}</td>
                    <td>
                      <span class="badge bg-info">{{ category.sale_margin_percentage }}%</span>
                    </td>
                    <td>
                      <span class="badge" :class="getMarginBadgeClass(product)">
                        {{ calculateActualMargin(product) }}%
                      </span>
                    </td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-primary" @click="productDetails(product.id)">
                        <i class="bi bi-eye"></i> Details
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-if="!loading" class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
            <button class="btn btn-dark text-uppercase" @click="$router.go(-1)">Back</button>
            <div class="me-sm-4 mb-sm-0 mb-3">
              <p class="mb-2 small">Rows per page</p>
              <select data="" class="form-select" v-model="dataTablePerPage">
                <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div>
              <Pagination v-model="dataTablePage" :pages="totalPages" :threshold="2">
                <template v-slot:next>
                  Next
                </template>
                <template v-slot:previous>
                  Prev
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import formatNumber from "@/utils/formatNumber";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import categoryService from "@/api/categoryService";

export default {
  name: "ProductsByCategory",

  data(){
    return {
      profile: {},
      loading: true,
      dataTable: [],
      category: {},
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    formatNumber,
    calculateActualMargin(product) {
      if (!product.buy || product.buy === 0) return 0;
      const margin = ((product.sell - product.buy) / product.buy) * 100;
      return margin.toFixed(2);
    },
    getMarginBadgeClass(product) {
      const actualMargin = parseFloat(this.calculateActualMargin(product));
      const expectedMargin = parseFloat(this.category.sale_margin_percentage || 0);
      
      if (actualMargin < expectedMargin - 1) {
        return 'bg-danger';
      } else if (Math.abs(actualMargin - expectedMargin) <= 1) {
        return 'bg-success';
      } else {
        return 'bg-warning';
      }
    },
    async getProducts() {
      this.category = await categoryService.getCategory(this.$route.params.id);
      this.dataTable = await categoryService.getProductsByCategory(this.$route.params.id);
      this.loading = false
    },
    productDetails(id) {
      router.push('/product/' + id)
    },
    addProduct(){
      router.push('/add-product')
    },
  },

  computed: {
    totalPages() {
      return Math.ceil(this.dataTable.length / this.dataTablePerPage) || 1;
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getProducts()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.product-row {
  transition: all 0.2s ease;
}

.product-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Avatar styles */
.product-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

.avatar-text {
  color: inherit;
}

/* Background color utilities */
.bg-primary-light {
  background-color: rgba(67, 97, 238, 0.1);
}

.bg-success-light {
  background-color: rgba(16, 185, 129, 0.1);
}

.bg-info-light {
  background-color: rgba(59, 130, 246, 0.1);
}

.bg-warning-light {
  background-color: rgba(245, 158, 11, 0.1);
}
</style>
